import { useCallback } from 'react';
import { usePantheon } from '../providers/PantheonProvider';
import type { ApiResponse } from '@pantheon/shared';

const STORAGE_KEY = 'pantheon_auth_token';

export function useApi() {
  const { apiUrl, authToken } = usePantheon();

  const request = useCallback(
    async <T>(
      path: string,
      options: RequestInit = {},
    ): Promise<ApiResponse<T>> => {
      // Use the latest token from localStorage (may have been refreshed)
      const currentToken = localStorage.getItem(STORAGE_KEY) ?? authToken;

      const url = `${apiUrl}${path}`;
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
          ...options.headers,
        },
      });

      // On 401, try to refresh the token and retry once
      if (response.status === 401) {
        try {
          const refreshRes = await fetch(`${apiUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: currentToken }),
          });
          const refreshBody = await refreshRes.json();

          if (refreshRes.ok && refreshBody.success && refreshBody.data?.token) {
            localStorage.setItem(STORAGE_KEY, refreshBody.data.token);

            // Retry the original request with the new token
            const retryResponse = await fetch(url, {
              ...options,
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${refreshBody.data.token}`,
                ...options.headers,
              },
            });

            const retryBody = await retryResponse.json();
            if (!retryResponse.ok) {
              return {
                success: false,
                error: retryBody.error ?? { code: 'INTERNAL_ERROR', message: retryResponse.statusText },
              };
            }
            return { success: true, data: retryBody.data ?? retryBody };
          }
        } catch {
          // Refresh failed — sign out
        }

        // Refresh failed — clear auth and redirect to login
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem('pantheon_auth_user');
        window.location.href = '/';
        return { success: false, error: { code: 'UNAUTHORIZED', message: 'Session expired' } };
      }

      const body = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: body.error ?? {
            code: 'INTERNAL_ERROR',
            message: response.statusText,
          },
        };
      }

      return { success: true, data: body.data ?? body };
    },
    [apiUrl, authToken],
  );

  const get = useCallback(
    <T>(path: string) => request<T>(path, { method: 'GET' }),
    [request],
  );

  const post = useCallback(
    <T>(path: string, data: unknown) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(data) }),
    [request],
  );

  const put = useCallback(
    <T>(path: string, data: unknown) =>
      request<T>(path, { method: 'PUT', body: JSON.stringify(data) }),
    [request],
  );

  const del = useCallback(
    <T>(path: string) => request<T>(path, { method: 'DELETE' }),
    [request],
  );

  return { get, post, put, del, request };
}
